import { Trash, UserPlus, X } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

interface SourceBulkActionsProps {
	selectedSns: string[];
	isPending: boolean;
	onAssign: () => void;
	onDelete: () => void;
	onClear: () => void;
}

export function SourceBulkActions({
	selectedSns,
	isPending,
	onAssign,
	onDelete,
	onClear,
}: SourceBulkActionsProps) {
	const { t } = useTranslation();
	const count = selectedSns.length;

	return (
		<div
			inert={count === 0 ? true : undefined}
			className={`flex h-full w-full items-center justify-between bg-muted px-4 transition-opacity duration-150 ${
				count === 0
					? "pointer-events-none opacity-0"
					: "pointer-events-auto opacity-100"
			}`}
		>
			<div className="flex items-center gap-2">
				<Button
					variant="ghost"
					size="icon"
					className="-ml-2 size-7 shrink-0"
					onClick={onClear}
					aria-label={t("mqttSources.clearSelection")}
				>
					<X className="size-3.5" aria-hidden="true" />
				</Button>
				<p className="text-sm text-muted-foreground whitespace-nowrap">
					<span className="font-semibold text-foreground tabular-nums">
						{count}
					</span>{" "}
					{count === 1
						? t("mqttSources.selectedOne")
						: t("mqttSources.selectedOther")}
				</p>
			</div>

			<div className="flex items-center gap-2">
				<AlertDialog>
					<AlertDialogTrigger asChild>
						<Button
							variant="ghost"
							className="gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
							disabled={isPending}
						>
							<Trash className="size-4 shrink-0" aria-hidden="true" />
							<span className="hidden sm:inline">
								{t("mqttSources.delete")}
							</span>
						</Button>
					</AlertDialogTrigger>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{t("mqttSources.deleteTitle")}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{t("mqttSources.bulkDeleteDescription", { count })}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>{t("mqttSources.cancel")}</AlertDialogCancel>
							<AlertDialogAction
								className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
								onClick={onDelete}
							>
								{t("mqttSources.delete")}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>

				<div className="h-4 w-px bg-border" />

				<Button
					variant="outline"
					className="gap-2"
					disabled={isPending}
					onClick={onAssign}
				>
					<UserPlus className="size-4 shrink-0" aria-hidden="true" />
					<span className="hidden sm:inline">
						{t("mqttSources.assignToReseller")}
					</span>
				</Button>
			</div>
		</div>
	);
}
